import { useEffect, useState } from 'react'
import { v4 } from 'uuid'
import { STAGE_COLORS } from '../../constants'
import { useStore } from '../../store'
import { PIPELINES_ACTIONS } from '../../store/pipelines'
import { authedAxios } from '../../utils'

const usePipelines = () => {
  const { state, dispatch } = useStore()

  const { pipelines, pipelinesExp } = state

  const [localCopy, setLocalCopy] = useState([])

  const [selectedPipelineId, setSelectedPipelineId] = useState(null)

  const [deletedPipelines, setDeletedPipelines] = useState([])

  const [isLoading, setIsLoading] = useState(true)
  const [hasLoadError, setHasLoadError] = useState(false)

  const selectedPipeline = localCopy.find((p) => p.id === selectedPipelineId)

  const getAllStages = (pipelines) =>
    pipelines.reduce((acc, item) => {
      return acc.concat(item.stages)
    }, [])

  const commitToStore = (data) =>
    dispatch({
      type: PIPELINES_ACTIONS.SET_PIPELINES,
      pipelines: data || localCopy,
    })

  const getPipelines = () => {
    if (pipelines?.length > 0 && Date.now() < pipelinesExp) {
      setLocalCopy(pipelines)
      setSelectedPipelineId(pipelines[0]?.id)
      setIsLoading(false)
      return Promise.resolve()
    }

    return authedAxios
      .get('/api/pipeline')
      .then((res) => {
        const dynamicPipelines = res.data.pipelines

        const pipelines = [
          {
            id: '__OPPORTUNITIES__',
            name: 'Opportunities',
            stages: res.data.opportunityStages.map((os) => ({
              ...os,
              pipelineId: '__OPPORTUNITIES__',
            })),
          },
          ...dynamicPipelines,
          {
            id: '__TICKETS__',
            name: 'Tickets',
            stages: res.data.ticketStages.map((ts) => ({
              ...ts,
              pipelineId: '__TICKETS__',
            })),
          },
        ]

        commitToStore(pipelines)
        setLocalCopy(pipelines)
        setSelectedPipelineId('__OPPORTUNITIES__')
      })
      .catch((e) => {
        setHasLoadError(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const savePipelines = () => {
    commitToStore()

    const originalStages = getAllStages(pipelines)
    const currentStages = getAllStages(localCopy)

    const deletedStages = originalStages.filter(
      (os) =>
        !currentStages.find(
          (cs) => os.id === cs.id && os.pipelineId === cs.pipelineId
        )
    )

    return authedAxios.put('/api/pipeline/setup', {
      deletedPipelines,
      deletedStages,
      pipelines: localCopy.map((p) => ({
        ...p,
        id: p.isNew ? undefined : p.id,
        stages: p.stages.map((s) => ({
          ...s,
          id: s.isNew ? undefined : s.id,
        })),
      })),
    })
  }

  const reset = () => {
    setLocalCopy(pipelines)
    setDeletedPipelines([])
  }

  const selectPipeline = (e) => {
    const { value } = e.target
    setSelectedPipelineId(value)
  }

  // const addPipeline = () => {
  //   const id = v4()
  //   setLocalCopy([
  //     ...localCopy,
  //     {
  //       name: `Pipeline ${localCopy.length + 1}`,
  //       isNew: true,
  //       stages: Array(MAX_ALLOWED_STAGES)
  //         .fill()
  //         .map((_, i) => ({
  //           isNew: true,
  //           id: v4(),
  //           name: `Stage ${i + 1}`,
  //           color: STAGE_COLORS[i],
  //         })),
  //       id,
  //     },
  //   ])

  //   setSelectedPipelineId(id)
  // }

  const updatePipeline = (newValues) => {
    const newPipelines = [...localCopy]
    const pipelineIndex = localCopy.map((p) => p.id).indexOf(selectedPipelineId)

    newPipelines.splice(pipelineIndex, 1, {
      ...localCopy[pipelineIndex],
      ...newValues,
    })

    setLocalCopy(newPipelines)
  }

  const moveStage = (index, direction) => {
    const { stages } = selectedPipeline
    const newStages = [...stages]

    const element = newStages.splice(index, 1)[0]
    newStages.splice(index + direction, 0, element)

    updatePipeline({ stages: newStages })
  }

  const removeStage = (stage) => {
    const { id } = stage
    const { stages } = selectedPipeline
    const newStages = [...stages].filter((s) => s.id !== id)

    updatePipeline({ stages: newStages })
  }

  const removePipeline = (pipeline) => {
    setSelectedPipelineId('')
    setLocalCopy(localCopy.filter((p) => p.id !== selectedPipelineId))
    if (!pipeline.isNew) setDeletedPipelines([...deletedPipelines, pipeline])
  }

  const addStage = () => {
    const { stages } = selectedPipeline

    const colors = stages.map((s) => s.color)

    const color = STAGE_COLORS.filter((c) => !colors.includes(c))[0]

    const newStages = [
      ...stages,
      {
        color,
        id: v4(),
        name: `Stage ${stages.length + 1}`,
        pipelineId: selectedPipelineId,
        isNew: true,
      },
    ]

    updatePipeline({ stages: newStages })
  }

  const updateStage = (id, newValues) => {
    const { stages } = selectedPipeline
    const newStages = [...stages].map((s) => {
      if (id !== s.id) return s

      return { ...s, ...newValues }
    })

    updatePipeline({ stages: newStages })
  }

  useEffect(() => {
    getPipelines()
  }, [])

  return {
    pipelines: localCopy,
    setPipelines: setLocalCopy,

    selectedPipelineId,
    deletedPipelines,

    selectedPipeline,

    setSelectedPipelineId,
    setDeletedPipelines,

    reset,
    updatePipeline,
    updateStage,
    moveStage,
    removeStage,
    removePipeline,
    addStage,
    selectPipeline,
    savePipelines,

    isLoading,
    hasLoadError,
  }
}

export default usePipelines
